import React from 'react';
import {
  Grid,
  Paper,
  FormControl,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  Typography,
  Pagination,
  Stack,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import ContractTableRow from './ContractTableRow';
import { PopupSave, PopupDelete } from 'views/Checkout';
import { usePopup } from 'hooks';
import { contractService } from 'services/contract';
import { storageService } from 'services/storage';
import { useQuery, useMutation } from 'react-query';
import { InputNumberFormat, Spinner } from 'components';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export const TableCellCustom = styled(TableCell)({
  border: 'none',
  padding: '8px 12px',
  color: '#252E58',
});

export const TableRowCustom = styled(TableRow)({
  borderBottom: '1px solid #EBECF0',
});

export const ButtonCustom = styled(Button)({
  background: 'none!important',
  paddingLeft: '0px!important',
  marginRight: '8px',
});

const schema = yup
  .object({
    startBlock: yup.number().positive().integer().required().min(1, "Start block can't be less than 1"),
  })
  .required();

function ContractWhislist() {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: { source: '', address: '', startBlock: 1 },
    resolver: yupResolver(schema),
  });
  //pagination
  const [page, setPage] = React.useState(1);
  const handleChange = async (event, value) => {
    await setPage(value);
    await refetchContracts();
  };
  const [isOpenSave, onOpenSave, onCloseSave] = usePopup(false);
  const [isOpenDelete, onOpenDelete, onCloseDelete] = usePopup(false);

  //get list contracts
  const {
    data: quotes = {},
    refetch: refetchContracts,
    isFetching,
  } = useQuery(['fetchContracts'], () => contractService.fetchContract({ offset: (page - 1) * 10, limit: 10 }));
  const contracts = quotes.contracts?.sort((a, b) => b.createdAt - a.createdAt);

  const { mutate: createContract, isLoading } = useMutation(contractService.createContracts, {
    onSuccess: (data, body) => {
      onRefresh();
    },
  });

  const handleClickSubmit = () => {
    handleSubmit((values) => createContract(values))();
  };

  const onRefresh = () => {
    reset({
      source: '',
      address: '',
      startBlock: 1,
    });
    refetchContracts();
  };

  //PopupSave
  const handleClickCancelPopupSave = () => {
    storageService.clearContract();
    storageService.clearSource();
    onCloseSave();
  };

  const handleConfirmPopupSave = () => {
    let address = storageService.getContract();
    let body = storageService.getSource();
    contractService.updateContracts(address, body).then(() => {
      onRefresh();
    });
    storageService.clearContract();
    storageService.clearSource();
    onCloseSave();
  };

  const info = {
    title: 'Save',
    content: 'Do you want to save your edit?',
  };
  //PopupDelete
  const handleClickCancelPopupDelete = () => {
    onCloseDelete();
    storageService.clearContract();
    onRefresh();
  };

  const handleConfirmPopupDelete = () => {
    let address = storageService.getContract();
    contractService.deleteContracts(address).then(() => {
      onRefresh();
    });
    storageService.clearContract();
    onCloseDelete();
  };
  return (
    <div>
      <Paper className='mb-4 rounded-lg p-8' style={{ minHeight: '175px' }}>
        <Grid container columns={12} spacing={3} mb={3}>
          <Grid item xl={3} md={3} xs={12}>
            <Controller
              name='source'
              defaultValue={''}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <label>Source name</label>
                  <TextField placeholder='Enter Source Name' {...field} />
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xl={6} md={6} xs={12}>
            <Controller
              name='address'
              defaultValue={''}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <label>Contract Address</label>
                  <TextField placeholder='Enter Contract Address' {...field} />
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xl={3} md={3} xs={12}>
            <Controller
              name='startBlock'
              defaultValue={1}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <label>Start Block</label>
                  <TextField
                    InputProps={{ inputComponent: InputNumberFormat }}
                    placeholder='Enter Start Block'
                    {...field}
                  />
                </FormControl>
              )}
            />
            {errors.startBlock && (
              <Typography component='p' className='text-red-600'>
                {errors.startBlock.message}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Button disabled={isFetching || isLoading} startIcon={<AddIcon />} onClick={handleClickSubmit}>
          Add new
        </Button>
      </Paper>
      <Paper className='rounded-lg p-8' style={{ minHeight: '392px' }}>
        <Spinner spinning={isFetching || isLoading}>
          <div className=' flex flex-col items-center overflow-x-auto'>
            <Table>
              <TableHead>
                <TableRowCustom>
                  <TableCellCustom>Source Name</TableCellCustom>
                  <TableCellCustom>Contract Address</TableCellCustom>
                  <TableCellCustom>ACTIONS</TableCellCustom>
                </TableRowCustom>
              </TableHead>
              <TableBody>
                {contracts &&
                  contracts.map((contract, index) => (
                    <ContractTableRow key={index} {...contract} onOpenSave={onOpenSave} onOpenDelete={onOpenDelete} />
                  ))}
              </TableBody>
            </Table>
            <Stack className='mt-3' spacing={2}>
              <Pagination
                count={Math.ceil(quotes.total / 10) || 1}
                variant='outlined'
                shape='rounded'
                page={page}
                onChange={handleChange}
              />
            </Stack>
          </div>
        </Spinner>
      </Paper>
      <Dialog open={isOpenSave} maxWidth='lg' className='w-81 h-43'>
        <PopupSave onCancel={handleClickCancelPopupSave} onConfirm={handleConfirmPopupSave} info={info} />
      </Dialog>
      <Dialog open={isOpenDelete} maxWidth='lg'>
        <PopupDelete onCancel={handleClickCancelPopupDelete} onConfirm={handleConfirmPopupDelete} />
      </Dialog>
    </div>
  );
}

export default ContractWhislist;
